/**
 * Fuzzy matching, so a filter answers what people actually type.
 *
 * Substring matching — what the controls on this surface started with — fails
 * the two things anyone does in a list of names. It misses initials ("rw" for
 * "Ridge Wallet"), and it cannot rank: "Ridge" typed against a list where one
 * client is "Ridge Wallet" and another is "Bridgeline" returns both, in
 * whatever order the array happened to be in.
 *
 * So: a subsequence match with a score, and the score is the point. The
 * ordering it produces is the ordering people expect —
 *
 *   1. the name that *starts* with what you typed
 *   2. the name where a *word* starts with it ("wallet" → "Ridge Wallet")
 *   3. the name that merely contains it
 *   4. the name whose letters appear in order ("rw", "rdgwlt")
 *
 * and inside each band, shorter names win, because a query is a larger share
 * of a short name than a long one.
 *
 * Deliberately small. This is a list of programs or clients — tens of items,
 * filtered on a keystroke — not a search index, and a scoring function you can
 * read in one sitting is worth more here than a library that handles
 * thousands.
 */

/** Consecutive matched characters are what make a match feel deliberate. */
const RUN_BONUS = 8;
/** A match at the start of a word is worth nearly as much as at the start. */
const WORD_BONUS = 12;

function isBoundary(text: string, at: number): boolean {
  if (at === 0) return true;
  const before = text[at - 1];
  return before === " " || before === "-" || before === "_" || before === ".";
}

/**
 * How well `text` matches `query`, or null for no match.
 *
 * Higher is better. The bands are spaced far enough apart that no amount of
 * bonus inside one can promote a result out of it — a subsequence hit never
 * outranks a prefix hit, however tidy its letters are.
 */
export function fuzzyScore(query: string, text: string): number | null {
  const needle = query.trim().toLowerCase();
  if (!needle) return 0;
  const haystack = text.toLowerCase();

  if (haystack.startsWith(needle)) return 4000 - haystack.length;

  const at = haystack.indexOf(needle);
  if (at > 0) {
    const band = isBoundary(haystack, at) ? 3000 : 2000;
    return band - at - haystack.length / 100;
  }

  // Subsequence: every character of the query, in order, anywhere.
  let score = 1000 - haystack.length / 100;
  let cursor = 0;
  let run = 0;
  for (const character of needle) {
    const found = haystack.indexOf(character, cursor);
    if (found === -1) return null;
    if (found === cursor && cursor > 0) run += 1;
    else run = 0;
    score += run * RUN_BONUS;
    if (isBoundary(haystack, found)) score += WORD_BONUS;
    cursor = found + 1;
  }
  return score;
}

/**
 * Rank a list by how well each item matches, dropping what does not.
 *
 * `text` pulls the primary string out of an item; `also` an optional secondary
 * one — a detail line, a slug — matched at a discount so a hit on the thing
 * being named always outranks a hit on its description.
 */
export function fuzzyRank<T>(
  query: string,
  items: T[],
  text: (item: T) => string,
  also?: (item: T) => string | undefined,
): T[] {
  if (!query.trim()) return items;
  const scored: { item: T; score: number }[] = [];
  for (const item of items) {
    const primary = fuzzyScore(query, text(item));
    const secondary = also ? fuzzyScore(query, also(item) ?? "") : null;
    const score =
      primary !== null ? primary : secondary !== null ? secondary * 0.4 : null;
    if (score !== null) scored.push({ item, score });
  }
  return scored.sort((a, b) => b.score - a.score).map((entry) => entry.item);
}
